import * as THREE from "three";
import { MAX_OBJECTS, PARTICLE_TEXTURE_WIDTH } from "./constants";

const MIPMAP_FACTOR = 4 / 3;

function bytesPerTexel(texture: THREE.Texture): number {
  if (texture.type === THREE.FloatType) {
    return 16;
  }
  if (texture.type === THREE.HalfFloatType) {
    return 8;
  }
  if (texture.type === THREE.UnsignedShortType || texture.type === THREE.UnsignedIntType) {
    return 4;
  }
  if (texture.format === THREE.RedFormat) {
    return 1;
  }
  if (texture.format === THREE.RGFormat) {
    return 2;
  }
  if (texture.format === THREE.RGBFormat) {
    return 3;
  }
  return 4;
}

function textureGpuBytes(texture: THREE.Texture): number {
  const image = texture.image as { width?: number; height?: number; depth?: number } | null | undefined;
  if (image === null || image === undefined || image.width === undefined || image.height === undefined) {
    return 0;
  }
  const depth = image.depth ?? 1;
  const faces = texture instanceof THREE.CubeTexture ? 6 : 1;
  const mips = texture.generateMipmaps ? MIPMAP_FACTOR : 1;
  return image.width * image.height * depth * bytesPerTexel(texture) * faces * mips;
}

function lightShadowMap(light: THREE.Light): THREE.Texture | null {
  if (!(light instanceof THREE.DirectionalLight || light instanceof THREE.SpotLight || light instanceof THREE.PointLight)) {
    return null;
  }
  return light.shadow.map?.texture ?? null;
}

function collectTextures(scene: THREE.Scene, out: Set<THREE.Texture>): void {
  if (scene.environment !== null) {
    out.add(scene.environment);
  }
  scene.traverse((obj: THREE.Object3D): void => {
    if (obj instanceof THREE.Light) {
      const shadowTex = lightShadowMap(obj);
      if (shadowTex !== null) {
        out.add(shadowTex);
      }
    }
    if (!(obj instanceof THREE.Mesh)) {
      return;
    }
    const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
    for (const material of materials) {
      if (material === null || material === undefined) {
        continue;
      }
      for (const value of Object.values(material)) {
        if (value instanceof THREE.Texture) {
          out.add(value);
        }
      }
    }
  });
}

function geometryGpuBytes(scene: THREE.Scene): number {
  let bytes = 0;
  scene.traverse((obj: THREE.Object3D): void => {
    if (!(obj instanceof THREE.Mesh || obj instanceof THREE.Line || obj instanceof THREE.Points)) {
      return;
    }
    const geo = obj.geometry;
    for (const attr of Object.values(geo.attributes) as THREE.BufferAttribute[]) {
      bytes += attr.array.byteLength;
    }
    if (geo.index !== null) {
      bytes += geo.index.array.byteLength;
    }
  });
  return bytes;
}

function gpgpuGpuBytes(): number {
  const texelBytes = 16;
  const texels = PARTICLE_TEXTURE_WIDTH * PARTICLE_TEXTURE_WIDTH;
  const variablePingPong = 4 * 2 * texels * texelBytes;
  const contact = 2 * MAX_OBJECTS * texelBytes;
  return variablePingPong + contact;
}

function composerGpuBytes(renderer: THREE.WebGLRenderer): number {
  const size = new THREE.Vector2();
  renderer.getSize(size);
  const pr = renderer.getPixelRatio();
  const w = Math.ceil(size.x * pr);
  const h = Math.ceil(size.y * pr);
  const screenRgba = w * h * 4;
  return screenRgba * 3;
}

function drawingBufferBytes(renderer: THREE.WebGLRenderer): number {
  const size = new THREE.Vector2();
  renderer.getSize(size);
  const pr = renderer.getPixelRatio();
  return Math.ceil(size.x * pr) * Math.ceil(size.y * pr) * 4;
}

/**
 * Approximate GPU memory from scene textures, geometry buffers, and fixed
 * off-scene allocations (GPGPU ping-pong, composer targets, shadow map).
 * WebGL exposes no exact VRAM counter; treat the result as an estimate.
 */
export function estimateVramBytes(renderer: THREE.WebGLRenderer, scene: THREE.Scene): number {
  const textures = new Set<THREE.Texture>();
  collectTextures(scene, textures);
  let bytes = geometryGpuBytes(scene);
  for (const texture of textures) {
    bytes += textureGpuBytes(texture);
  }
  bytes += gpgpuGpuBytes();
  bytes += composerGpuBytes(renderer);
  bytes += drawingBufferBytes(renderer);
  return bytes;
}

export function formatVramMb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}
